// Considered: ChipAtom — rejected because it wraps PrimeReact `Chip`, whose body is not a press
// (its only button removes the chip). Considered: TagAtom's interactive form — rejected because
// it is a toggle (`aria-pressed` on every press, and this one navigates), hovers by moving
// (`transform: translateY`), carries the marketplace tone palette, sets no touch-target floor,
// and draws its own `2px solid` focus ring instead of `--focus-ring`. This is the prototype's
// `LocChip` (`type-composer.jsx:482-493`): a fixed language glyph, a `done/total` readout, and
// one job — open Languages for the string it sits beside. Three composer rows carried their
// own copy of it (identity, property, relationship) until it lived here once.
import React from 'react';
import './TranslationCoverageChipAtom.scss';

export interface TranslationCoverageChipAtomProps {
  /** Locales in which the string has been reviewed. */
  done: number;
  /**
   * Locales the string is expected in. `0` means there is no string yet to
   * translate — the chip reads `—` rather than `0/0`.
   */
  total: number;
  /**
   * What the press is for, e.g. "Translations for Vendor code". It is the hover
   * statement and the first part of the accessible name; the readout is the rest,
   * so a screen reader hears "Translations for Vendor code, 0/1" — the visible
   * text stays in the name (ux-craft A3).
   */
  label: string;
  /** Opens Languages for the string. The chip is only worth rendering when this exists. */
  onClick: () => void;
  className?: string;
  'data-testid'?: string;
  /** Parity self-description (see visual-fidelity.md) — the contract datum this chip renders. */
  'data-datum'?: string;
  'data-kind'?: string;
}

const TranslationCoverageChipAtom: React.FC<TranslationCoverageChipAtomProps> = ({
  done,
  total,
  label,
  onClick,
  className,
  'data-testid': testId,
  'data-datum': dataDatum,
  'data-kind': dataKind = 'chip',
}) => (
  <button
    type="button"
    className={['translation-coverage-chip', className].filter(Boolean).join(' ')}
    title={label}
    onClick={onClick}
    data-testid={testId}
    data-datum={dataDatum}
    data-kind={dataKind}
  >
    <i className="pi pi-language" aria-hidden="true" />
    <span className="translation-coverage-chip__label">{label}, </span>
    <span className="translation-coverage-chip__readout">{total > 0 ? `${done}/${total}` : '—'}</span>
  </button>
);

export default TranslationCoverageChipAtom;
